import React from "react";

import { graphql, useMutation } from "react-relay";

import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { TodoEditTeamsPopoverSetTeamMutation } from "./__generated__/TodoEditTeamsPopoverSetTeamMutation.graphql";
import { TodoTeamsPopover } from "./TodoTeamsPopover";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";

type TodoEditTeamsPopoverProps = {
  todoId: string;
  selectedTeamIds: string[];
  assignedRoles: readonly {
    readonly id: string;
    readonly role: {
      readonly id: string;
      readonly team: { readonly id: string };
    };
  }[];
  visible: boolean;
  onClose: () => void;
  setStatus: (key: string, status: "saving" | "saved" | "error") => void;
};

export function TodoEditTeamsPopover(props: TodoEditTeamsPopoverProps) {
  const { currentEmployeeId } = usePersistedEmployeeId();

  const [updateTeams] = useMutation<TodoEditTeamsPopoverSetTeamMutation>(
    graphql`
      mutation TodoEditTeamsPopoverSetTeamMutation(
        $id: ID!
        $teamIds: [ID!]!
        $allTeams: Boolean!
        $employeeId: ID
      ) @raw_response_type {
        todo(todoId: $id, employeeId: $employeeId) {
          updateTeams(newTeamIds: $teamIds, setAllTeams: $allTeams) {
            id
            allTeams
            teams {
              id
            }
            assignedRoles {
              id
              role {
                id
              }
            }
          }
        }
      }
    `
  );

  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => (
        <TodoTeamsPopover
          fetchKey={fk}
          visible={props.visible}
          onClose={props.onClose}
          selectedTeamIds={props.selectedTeamIds}
          setSelectedTeamIds={(selectedTeamIds) => {
            const allTeams = selectedTeamIds.length === 0;
            const newTeamIdSet = new Set(selectedTeamIds);
            updateTeams({
              variables: {
                id: props.todoId,
                teamIds: selectedTeamIds,
                allTeams,
                employeeId: currentEmployeeId,
              },
              optimisticResponse: {
                todo: {
                  updateTeams: {
                    id: props.todoId,
                    allTeams,
                    teams: selectedTeamIds.map((id) => ({ id })),
                    assignedRoles: allTeams
                      ? props.assignedRoles.map((ar) => ({
                          id: ar.id,
                          role: { id: ar.role.id },
                        }))
                      : props.assignedRoles
                          .filter((ar) => newTeamIdSet.has(ar.role.team.id))
                          .map((ar) => ({
                            id: ar.id,
                            role: { id: ar.role.id },
                          })),
                  },
                },
              },
            });
          }}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
